import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { appRoutes, AppRoute, SidebarSection } from "../../config/routes";
import {
  useMyWorkspaces,
  useSwitchWorkspace,
} from "../../hooks/queries/useWorkspaces";
import { formatNumber } from "../../utils/formatters";
import {
  Aside,
  Backdrop,
  CollapseButton,
  Item,
  Scroll,
  SectionLabel,
  Separator,
  SubItem,
  WorkspaceCard,
  WorkspaceMenu,
} from "./Sidebar.styles";
import {
  AnalyticsIcon,
  AssetsIcon,
  BillingIcon,
  BroadcastsIcon,
  ChevronIcon,
  CollapseIcon,
  DashboardIcon,
  DocsIcon,
  DomainsIcon,
  ManagersIcon,
  IntegrationsIcon,
  RocketIcon,
  TeamIcon,
  WorkspaceIcon,
} from "./sidebarIcons";

export const COLLAPSED_KEY = "kemblo_sidebar_collapsed";

/**
 * Reads the remembered collapse preference.
 *
 * <p>Exported because the page margin has to match the sidebar width, so the layout owns this
 * state and hands it down rather than the sidebar keeping it to itself.
 */
export const readSidebarCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    // Private mode and blocked site data both throw here; a sidebar preference is not
    // worth breaking the whole shell over.
    return false;
  }
};

/** Groups that expand to reveal the routes inside them. */
const GROUPS: {
  section: SidebarSection;
  i18nKey: string;
  fallback: string;
  icon: JSX.Element;
}[] = [
  {
    section: "assets",
    i18nKey: "nav:traffic_assets",
    fallback: "Traffic Assets",
    icon: <AssetsIcon />,
  },
  {
    section: "analytics",
    i18nKey: "nav:analytics",
    fallback: "Analytics",
    icon: <AnalyticsIcon />,
  },
];

/** Icons for the flat routes, keyed by path so the sidebar owns its own iconography. */
const ROUTE_ICONS: Record<string, JSX.Element> = {
  dashboard: <DashboardIcon />,
  "getting-started": <RocketIcon />,
  broadcasts: <BroadcastsIcon />,
  docs: <DocsIcon />,
  team: <TeamIcon />,
  domains: <DomainsIcon />,
  managers: <ManagersIcon />,
  integrations: <IntegrationsIcon />,
  billing: <BillingIcon />,
};

interface Props {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

/**
 * The primary navigation, moved out of the header.
 *
 * <p>The header could not hold this much: five destinations, three of them groups, plus the
 * workspace switcher and seven controls. Everything that answers "where do I go" now lives here,
 * and the header keeps only what acts on the current session.
 *
 * <p>Collapsing is remembered per browser. Groups auto-expand when the current route is inside
 * them, so a deep link never lands you on a page whose nav entry is hidden.
 */
export const Sidebar = ({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
}: Props) => {
  const { t } = useTranslation(["nav", "onboarding", "common"]);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || "en";

  const { data: myWorkspaces } = useMyWorkspaces();
  const { mutate: switchWorkspace } = useSwitchWorkspace();
  const activeWorkspace = myWorkspaces?.find((ws) => ws.active);

  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);

  const routesIn = (section: SidebarSection) =>
    appRoutes.filter((route) => route.section === section);

  const mainRoutes = routesIn("main");
  const settingsRoutes = routesIn("settings");

  const isActive = (path: string) =>
    location.pathname === `/${currentLang}/${path}` ||
    location.pathname.startsWith(`/${currentLang}/${path}/`);

  // A group counts as open when the user opened it or when the current page lives inside it.
  const activeGroup = useMemo(
    () =>
      GROUPS.find((group) =>
        routesIn(group.section).some((route) => isActive(route.path)),
      )?.section ?? null,
    [location.pathname],
  );

  // A set rather than a single value: groups open independently, so you can keep Traffic Assets
  // and Analytics both unfolded and move between them without one closing the other.
  const [openGroups, setOpenGroups] = useState<Set<SidebarSection>>(
    () => new Set(activeGroup ? [activeGroup] : []),
  );

  // Navigating into a group reveals it, and never closes the ones already open.
  useEffect(() => {
    if (!activeGroup) return;
    setOpenGroups((open) =>
      open.has(activeGroup) ? open : new Set(open).add(activeGroup),
    );
  }, [activeGroup]);

  const toggleGroup = (section: SidebarSection) =>
    setOpenGroups((open) => {
      const next = new Set(open);
      if (!next.delete(section)) next.add(section);
      return next;
    });

  // Close the workspace menu on an outside click, the way every other menu in the app behaves.
  useEffect(() => {
    if (!workspaceOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!workspaceRef.current?.contains(event.target as Node)) {
        setWorkspaceOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [workspaceOpen]);

  const go = (route: AppRoute) => {
    if (route.openInNewTab) {
      window.open(`/${route.path}`, "_blank", "noreferrer");
    } else {
      navigate(`/${currentLang}/${route.path}`);
    }
    onCloseMobile();
  };

  const label = (route: AppRoute) =>
    route.i18nKey ? t(route.i18nKey, route.label) : route.label;

  const renderFlat = (route: AppRoute) => (
    <Item
      key={route.path}
      type="button"
      $active={isActive(route.path)}
      $collapsed={collapsed}
      onClick={() => go(route)}
      title={collapsed ? label(route) : undefined}
      aria-current={isActive(route.path) ? "page" : undefined}
    >
      {ROUTE_ICONS[route.path] ?? <DashboardIcon />}
      <span className="label">{label(route)}</span>
    </Item>
  );

  return (
    <>
      <Backdrop $open={mobileOpen} onClick={onCloseMobile} />

      <Aside $collapsed={collapsed} $mobileOpen={mobileOpen}>
        {/* No brand mark here: the wordmark stays in the header, so the workspace switcher is
            the first thing in the sidebar on every page. */}
        <div
          ref={workspaceRef}
          style={{ position: "relative", marginTop: "0.85rem" }}
        >
          <WorkspaceCard
            type="button"
            $collapsed={collapsed}
            onClick={() => setWorkspaceOpen((open) => !open)}
            aria-expanded={workspaceOpen}
            title={activeWorkspace?.name}
          >
            <WorkspaceIcon />
            {!collapsed && (
              <span className="meta">
                <span className="name">
                  {activeWorkspace?.name ?? t("nav:workspace", "Workspace")}
                </span>
                {activeWorkspace?.balanceUsd != null && (
                  <span className="balance">
                    ${formatNumber(activeWorkspace.balanceUsd)}
                  </span>
                )}
              </span>
            )}
            {!collapsed && <ChevronIcon />}
          </WorkspaceCard>

          {workspaceOpen && myWorkspaces && myWorkspaces.length > 0 && (
            <WorkspaceMenu $collapsed={collapsed}>
              {myWorkspaces.map((ws) => (
                <button
                  key={ws.id}
                  type="button"
                  aria-current={ws.active}
                  onClick={() => {
                    if (!ws.active) switchWorkspace(ws.id);
                    setWorkspaceOpen(false);
                  }}
                >
                  {ws.name}
                </button>
              ))}
            </WorkspaceMenu>
          )}
        </div>

        <Scroll>
          {mainRoutes.map(renderFlat)}

          {GROUPS.map((group) => {
            const routes = routesIn(group.section);
            if (routes.length === 0) return null;

            const groupActive = routes.some((route) => isActive(route.path));
            const expanded = !collapsed && openGroups.has(group.section);

            return (
              <div key={group.section}>
                <Item
                  type="button"
                  $active={groupActive && !expanded}
                  $collapsed={collapsed}
                  title={
                    collapsed ? t(group.i18nKey, group.fallback) : undefined
                  }
                  aria-expanded={expanded}
                  onClick={() => {
                    if (collapsed) {
                      // No room to expand in place, so go to the group's first page.
                      go(routes[0]);
                      return;
                    }
                    toggleGroup(group.section);
                  }}
                >
                  {group.icon}
                  <span className="label">
                    {t(group.i18nKey, group.fallback)}
                  </span>
                  <span
                    className="chevron"
                    style={{
                      transform: expanded ? "rotate(180deg)" : undefined,
                    }}
                  >
                    <ChevronIcon />
                  </span>
                </Item>

                {expanded &&
                  routes.map((route) => (
                    <SubItem
                      key={route.path}
                      type="button"
                      $active={isActive(route.path)}
                      onClick={() => go(route)}
                      aria-current={isActive(route.path) ? "page" : undefined}
                    >
                      <span className="label">{label(route)}</span>
                    </SubItem>
                  ))}
              </div>
            );
          })}

          <Separator />
          <SectionLabel $collapsed={collapsed}>
            {t("nav:settings", "Settings")}
          </SectionLabel>

          {settingsRoutes.map(renderFlat)}
        </Scroll>

        <CollapseButton
          type="button"
          onClick={onToggleCollapsed}
          aria-label={
            collapsed
              ? t("nav:expand_sidebar", "Expand sidebar")
              : t("nav:collapse_sidebar", "Collapse sidebar")
          }
        >
          <span
            style={{
              transform: collapsed ? "rotate(180deg)" : undefined,
              display: "inline-flex",
            }}
          >
            <CollapseIcon />
          </span>
          {!collapsed && t("nav:collapse_sidebar", "Collapse sidebar")}
        </CollapseButton>
      </Aside>
    </>
  );
};
